<template>
  <Modal :show="true" @close-modal="$emit('close-modal')" class="add-member-modal">
    <div class="add-member">
      <form @submit.prevent="submitForm" class="add-member__form">
        <div class="add-member__header">
          <div class="add-member__icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <h1 class="add-member__title">Add New Team Member</h1>
            <p class="add-member__subtitle">Add a new team member and choose a role that best suits them</p>
          </div>
        </div>

        <div class="add-member__grid">
          <div class="form-group">
            <label class="form-group__label" for="fname">First Name</label>
            <input
              id="fname"
              v-model="form.fname"
              placeholder="Enter first name"
              type="text"
              class="form-group__input"
              :class="{ 'form-group__input--error': errors.fname && submitted }"
              required
            />
            <p v-if="errors.fname && submitted" class="form-group__error">{{ errors.fname }}</p>
          </div>

          <div class="form-group">
            <label class="form-group__label" for="lname">Last Name</label>
            <input
              id="lname"
              v-model="form.lname"
              type="text"
              placeholder="Enter last name"
              class="form-group__input"
              :class="{ 'form-group__input--error': errors.lname && submitted }"
              required
            />
            <p v-if="errors.lname && submitted" class="form-group__error">{{ errors.lname }}</p>
          </div>

          <div class="form-group">
            <label class="form-group__label" for="email">Email Address</label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              placeholder="Enter email address"
              class="form-group__input"
              :class="{ 'form-group__input--error': errors.email && submitted }"
              required
            />
            <p v-if="errors.email && submitted" class="form-group__error">{{ errors.email }}</p>
          </div>

          <div class="form-group">
            <label class="form-group__label" for="role">Role</label>
            <div class="form-group__select-wrapper">
              <select
                id="role"
                v-model="form.role"
                class="form-group__select"
                :class="{ 'form-group__select--error': errors.role && submitted }"
                required
              >
                <option value="" disabled>Select role</option>
                <option v-for="(role, index) in roles" :key="index" :value="role.name">
                  {{ role.name }}
                </option>
              </select>
              <svg class="form-group__select-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <p v-if="errors.role && submitted" class="form-group__error">{{ errors.role }}</p>
          </div>
        </div>

        <div v-if="form.role" class="permissions-toggle">
          <button
            type="button"
            @click="showPermissions = !showPermissions"
            class="permissions-toggle__btn"
          >
            <span>{{ showPermissions ? "Hide" : "View" }} Role Privileges</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              :class="{ 'permissions-toggle__icon--open': showPermissions }"
              class="permissions-toggle__icon"
            >
              <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <div v-if="showPermissions && form.role" class="permissions-preview">
          <div v-if="filteredServices.length === 0" class="permissions-preview__empty">
            No permissions assigned to this role
          </div>
          <div
            v-for="service in filteredServices"
            :key="service.service"
            class="permission-item"
          >
            <div class="permission-item__header" @click="toggleServiceExpand(service)">
              <div class="permission-item__left">
                <div class="checkbox-display" :class="{ 'checkbox-display--checked': hasServicePermission(service) }">
                  <svg v-if="hasServicePermission(service)" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.5 8.25L2.25 6L1.5 6.75L4.5 9.75L10.5 3.75L9.75 3L4.5 8.25Z" fill="currentColor"/>
                  </svg>
                </div>
                <span class="permission-item__name">{{ formatServiceName(service.service) }}</span>
              </div>
              <svg
                class="permission-item__expand"
                :class="{ 'permission-item__expand--open': service.expanded }"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="permission-item__actions" v-if="service.expanded">
              <div
                v-for="action in service.actions"
                :key="action"
                class="action-display"
              >
                <span class="action-display__label">{{ formatActionName(action) }}</span>
                <div
                  class="toggle-display"
                  :class="{ 'toggle-display--active': hasActionPermission(service.service, action) }"
                >
                  <span class="toggle-display__handle"></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="add-member__actions">
          <button type="button" @click="$emit('close-modal')" class="btn-secondary">
            Cancel
          </button>
          <BtnComponent type="submit" title="Add Member" :loading="loading" />
        </div>
      </form>
    </div>
  </Modal>
</template>

<script setup>
import Modal from "@/components/general/Modal";
import BtnComponent from "../general/BtnComponent.vue";
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useGetters } from "@/composables/useMapper";

const emit = defineEmits(["close-modal", "success"]);
const store = useStore();

const props = defineProps({
  source: {
    type: String,
    default: "company",
  },
});
const loading = computed(() => store.getters["loading"]);
const orgDetails = computed(
  () => store.getters["organisationModule/organisationDetails"]
);

const { iamGroups } = useGetters("organisationModule", ["iamGroups"]);

const roles = computed(() => iamGroups.value?.docs || []);

const form = reactive({
  fname: "",
  lname: "",
  email: "",
  role: "",
});

const errors = reactive({
  fname: "",
  lname: "",
  email: "",
  role: "",
});
const showPermissions = ref(false);
const rolePermissions = ref([]);

const allServices = ref([
  {
    service: "deal",
    actions: ["addDeal", "updateDeal", "viewDeal"],
    expanded: false,
  },
  {
    service: "member",
    actions: ["addMember", "updateMember", "viewMember"],
    expanded: false,
  },
  {
    service: "wallet",
    actions: [
      "fundWallet",
      "withdrawWallet",
      "buyWallet",
      "sellWallet",
      "viewWallet",
    ],
    expanded: false,
  },
  {
    service: "team",
    actions: [
      "addMember",
      "updateMember",
      "viewMember",
      "addGroup",
      "updateGroup",
      "viewGroup",
    ],
    expanded: false,
  },
  {
    service: "kyc",
    actions: ["addKyc", "updateKyc", "viewKyc"],
    expanded: false,
  },
  {
    service: "bank",
    actions: ["addBank", "updateBank", "viewBank"],
    expanded: false,
  },
]);

const filteredServices = computed(() => {
  if (rolePermissions.value.includes("*:*")) {
    return allServices.value;
  }

  return allServices.value.filter((service) => {
    if (rolePermissions.value.includes(`${service.service}:*`)) {
      return true;
    }

    return service.actions.some((action) =>
      rolePermissions.value.includes(`${service.service}:${action}`)
    );
  });
});

const submitted = ref(false);

function validateForm() {
  let isValid = true;

  errors.fname = "";
  errors.lname = "";
  errors.email = "";
  errors.role = "";

  if (!form.fname.trim()) {
    errors.fname = "First name is required";
    isValid = false;
  }

  if (!form.lname.trim()) {
    errors.lname = "Last name is required";
    isValid = false;
  }

  if (!form.email.trim()) {
    errors.email = "Email is required";
    isValid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = "Please enter a valid email";
    isValid = false;
  }

  if (!form.role) {
    errors.role = "Please select a role";
    isValid = false;
  }

  return isValid;
}

async function submitForm() {
  submitted.value = true;

  if (!validateForm()) {
    return;
  }

  const role = roles.value.find((r) => r.name === form.role);

  try {
    await store.dispatch("organisationModule/addMemberToTeam", {
      payload: {
        first_name: form.fname,
        last_name: form.lname,
        email: form.email,
        groups: [role.id],
        source: props.source,
      },
      id: orgDetails.value._id,
    });
    emit("success");
  } catch (error) {
    console.error("Failed to add team member:", error);
  }
}

const formatServiceName = (service) => {
  return service.charAt(0).toUpperCase() + service.slice(1);
};

const formatActionName = (action) => {
  return action
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());
};

const toggleServiceExpand = (service) => {
  service.expanded = !service.expanded;
};

const extractRolePermissions = () => {
  rolePermissions.value = [];

  if (!form.role) return;

  const selectedRoleObj = roles.value.find((r) => r.name === form.role);

  if (!selectedRoleObj) return;

  if (
    selectedRoleObj.permissions &&
    Array.isArray(selectedRoleObj.permissions)
  ) {
    selectedRoleObj.permissions.forEach((permissionObj) => {
      if (
        permissionObj.actions &&
        Array.isArray(permissionObj.actions) &&
        permissionObj.effect === "ALLOW"
      ) {
        rolePermissions.value.push(...permissionObj.actions);
      }
    });
  }
};

const hasServicePermission = (service) => {
  const hasAllStarPermission =
    rolePermissions.value.includes(`${service.service}:*`) ||
    rolePermissions.value.includes("*:*");
  if (hasAllStarPermission) return true;

  return service.actions.some((action) =>
    rolePermissions.value.includes(`${service.service}:${action}`)
  );
};

const hasActionPermission = (service, action) => {
  const actionString = `${service}:${action}`;
  const hasAllStarPermission =
    rolePermissions.value.includes(`${service}:*`) ||
    rolePermissions.value.includes("*:*");

  return hasAllStarPermission || rolePermissions.value.includes(actionString);
};

watch(
  () => form.role,
  (newRole) => {
    if (newRole) {
      extractRolePermissions();
      showPermissions.value = true;
    } else {
      rolePermissions.value = [];
      showPermissions.value = false;
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.add-member {
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
  
  &__form {
    width: 100%;
  }
  
  &__header {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 32px;
  }
  
  &__icon {
    width: 48px;
    height: 48px;
    background: #EEF4FF;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #4D84FF;
    flex-shrink: 0;
  }
  
  &__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 20px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 4px 0;
  }
  
  &__subtitle {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }
  
  &__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    
    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  }
  
  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid #E2E8F0;
  }
}

.form-group {
  &__label {
    display: block;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
    margin-bottom: 8px;
  }
  
  &__input {
    width: 100%;
    padding: 12px 16px;
    background: #ffffff;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
    transition: all 0.2s ease;
    
    &::placeholder {
      color: #94A3B8;
    }
    
    &:focus {
      outline: none;
      border-color: #4D84FF;
      box-shadow: 0 0 0 3px rgba(77, 132, 255, 0.1);
    }
    
    &--error {
      border-color: #EF4444;
    }
  }
  
  &__select-wrapper {
    position: relative;
  }
  
  &__select {
    width: 100%;
    padding: 12px 40px 12px 16px;
    background: #ffffff;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
    cursor: pointer;
    appearance: none;
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #4D84FF;
      box-shadow: 0 0 0 3px rgba(77, 132, 255, 0.1);
    }
    
    &--error {
      border-color: #EF4444;
    }
  }
  
  &__select-icon {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #64748B;
    pointer-events: none;
  }
  
  &__error {
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #EF4444;
    margin: 6px 0 0 0;
  }
}

.permissions-toggle {
  margin: 24px 0;
  
  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 12px;
    background: transparent;
    border: none;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #4D84FF;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      color: #3B6FE8;
    }
  }
  
  &__icon {
    transition: transform 0.2s ease;
    
    &--open {
      transform: rotate(180deg);
    }
  }
}

.permissions-preview {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  
  &__empty {
    text-align: center;
    padding: 24px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
  }
}

.permission-item {
  background: #ffffff;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  margin-bottom: 12px;
  overflow: hidden;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    cursor: pointer;
    transition: background 0.2s ease;
    
    &:hover {
      background: #F8FAFC;
    }
  }
  
  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  &__name {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
    user-select: none;
  }
  
  &__expand {
    color: #64748B;
    transition: transform 0.2s ease;
    
    &--open {
      transform: rotate(180deg);
    }
  }
  
  &__actions {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 10px;
    padding: 12px 16px;
    background: #F8FAFC;
    border-top: 1px solid #E2E8F0;
  }
}

.checkbox-display {
  width: 20px;
  height: 20px;
  background: #ffffff;
  border: 2px solid #E2E8F0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &--checked {
    background: #10B981;
    border-color: #10B981;
    color: #ffffff;
  }
}

.action-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  
  &__label {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #0F172A;
    user-select: none;
  }
}

.toggle-display {
  width: 36px;
  height: 20px;
  background: #E2E8F0;
  border-radius: 10px;
  position: relative;
  
  &--active {
    background: #10B981;
    
    .toggle-display__handle {
      transform: translateX(16px);
    }
  }
  
  &__handle {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    background: #ffffff;
    border-radius: 50%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease;
  }
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  background: #F1F5F9;
  border: none;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #64748B;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #E2E8F0;
    color: #0F172A;
  }
}
</style>